import { runAllSequentially, Task, TaskStatus } from './task'
import { Test } from './test'
import { TestReport } from './test-report'

import { VideoFrameChecker } from './video'

import { StatisticsAggregate, getReadableSizeString, round, now } from './stats'

import {
  Call,
  filterConfig,
  gatherCandidates,
  setTimeoutWithProgressBar,
} from './call'

//
// ConnectivityTest
//

export const ConnectivityTest = (
  test: Test,
  iceCandidateFilter: (candidate: RTCIceCandidate) => boolean,
  config?: any,
) => {
  test.reportInfo(
    'Use IceServers ' + JSON.stringify(config?.iceServers[0]?.urls),
  )

  try {
    const call = new Call(test, config)
    call.setIceCandidateFilter(function (candidate: RTCIceCandidate) {
      //test.reportInfo('candidate: ' + candidate.type + '/' +  candidate.protocol + ' ' + candidate.address);
      return iceCandidateFilter(candidate)
    })

    const ch1 = call.pc1.createDataChannel('ConnectivityTest')
    ch1.addEventListener('open', function () {
      ch1.send('hello')
    })
    ch1.addEventListener('message', function (event) {
      if (event.data !== 'world') {
        test.reportFatal('Invalid Data')
        call.close()
      } else {
        test.reportSuccess('Data successfully transmitted between peers.')
        test.done()
        call.close()
      }
    })

    call.pc2.addEventListener('datachannel', function (event) {
      const ch2 = event.channel
      ch2.addEventListener('message', function (event) {
        if (event.data !== 'hello') {
          test.reportFatal('Invalid Data')
          call.close()
        } else {
          ch2.send('world')
        }
      })
    })
    call.establishConnection()

    // Timout
    const testDurationMs = 10000
    setTimeoutWithProgressBar(
      test,
      testDurationMs,
      test.reportFatal.bind(test, 'ConnectivityTest: Timed out'),
    )
  } catch (errCall) {
    test.reportFatal(errCall as Error)
  }
}

// Set up a datachannel between two peers through a local IP address
// and verify data can be transmitted and received
// (packets should not leave the machine running the test)
export const HostConnectivityTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    ConnectivityTest.bind(null, test, Call.isHost),
    test.reportFatal.bind(test),
  )
}

export const CandidateConnectivityTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    ConnectivityTest.bind(null, test, Call.isNotHostCandidate),
    test.reportFatal.bind(test),
  )
}

// Set up a datachannel between two peers through a public IP address
// and verify data can be transmitted and received
// (packets should stay on the link if behind a router doing NAT)
export const ReflexiveConnectivityTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    ConnectivityTest.bind(null, test, Call.isReflexiveOrHost),
    test.reportWarning.bind(test),
  )
}

// Set up a datachannel between two peers through a relay
// and verify data can be transmitted and received
// (packets travel through the public internet)
export const RelayConnectivityTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    ConnectivityTest.bind(null, test, Call.isRelay),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const TcpConnectivityTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    function (config: any) {
      filterConfig(config, 'tcp')
      gatherCandidates(test, config, null, Call.isTcp)
    },
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via UDP to a TURN server
// Get a TURN config, and try to get a relay candidate using UDP.
export const UdpConnectivityTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    function (config: any) {
      filterConfig(config, 'udp')
      gatherCandidates(test, config, null, Call.isUdp)
    },
    test.reportFatal.bind(test),
  )
}

// Test whether it is IPv6 enabled (TODO: test IPv6 to a destination).
// Turn on IPv6, and try to get an IPv6 host candidate.
export const HasIpv6CandidatesTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    () => {
      gatherCandidates(
        test,
        null,
        {
          optional: [
            {
              googIPv6: true,
            },
          ],
        },
        Call.isIpv6,
      )
    },
    test.reportWarning.bind(test),
  )
}

//
// DataChannelThroughputTest
//

// Creates a loopback via relay candidates and tries to send as many packets
// with 1024 chars as possible while keeping dataChannel bufferedAmmount above
// zero.
export const DataChannelThroughputTest = (
  test: Test,
  iceCandidateFilter: (candidate: RTCIceCandidate) => boolean,
  config: any,
) => {
  test.reportInfo(
    'Use IceServers ' + JSON.stringify(config?.iceServers[0]?.urls),
  )

  const testDurationSeconds = 10
  const testDurationMs = testDurationSeconds * 1000
  const testTimeoutMs = 15 * 1000

  let call: Call
  let receiveChannel: RTCDataChannel | null
  let senderChannel: RTCDataChannel
  let startTime = 0
  let sentPayloadBytes = 0
  let receivedPayloadBytes = 0
  let stopSending = false

  let samplePacket = ''
  for (let i = 0; i !== 1024; ++i) {
    samplePacket += 'h'
  }

  const maxNumberOfPacketsToSend = 1
  const bytesToKeepBuffered = 1024 * maxNumberOfPacketsToSend

  let lastBitrateMeasureTime = 0
  let lastReceivedPayloadBytes = 0

  //
  function onMessageReceived(event: MessageEvent) {
    receivedPayloadBytes += event.data.length
    const time = now()

    if (time - lastBitrateMeasureTime >= 1000) {
      let bitrate =
        (receivedPayloadBytes - lastReceivedPayloadBytes) /
        (time - lastBitrateMeasureTime)
      bitrate = Math.round(bitrate * 1000 * 8)

      test.reportInfo(`Transmitting at ${getReadableSizeString(bitrate)}.`)
      lastReceivedPayloadBytes = receivedPayloadBytes
      lastBitrateMeasureTime = time
    }

    if (stopSending) {
      if (sentPayloadBytes === receivedPayloadBytes) {
        const elapsedTime = Math.round((time - startTime) * 10) / 10000.0
        test.reportInfo(
          `Total sent: ${getReadableSizeString(
            sentPayloadBytes * 8,
          )} in ${elapsedTime} seconds.`,
        )
        test.reportSuccess(
          `Total received: ${getReadableSizeString(
            receivedPayloadBytes * 8,
          )} in ${elapsedTime} seconds.`,
        )
        call.close()
        test.done()
      }
    }
  }

  function onReceiverChannel(event: RTCDataChannelEvent) {
    receiveChannel = event.channel
    receiveChannel.addEventListener('message', onMessageReceived)
  }

  function sendingStep() {
    const time = now()
    if (!startTime) {
      startTime = time
      lastBitrateMeasureTime = time
    }

    for (let i = 0; i !== maxNumberOfPacketsToSend; ++i) {
      if (senderChannel.bufferedAmount >= bytesToKeepBuffered) {
        break
      }
      try {
        if (senderChannel.readyState === 'open') {
          senderChannel.send(samplePacket)
          sentPayloadBytes += samplePacket.length
        }
      } catch (err) {
        test.reportFatal(`DataChannelThroughputTest: ${err}`)
      }
    }

    const duration = time - startTime
    if (duration >= testDurationMs) {
      test.setProgress(100)
      stopSending = true
    } else {
      test.setProgress((100 / testDurationMs) * duration)
      setTimeout(sendingStep, 1)
    }
  }

  function testTimout() {
    stopSending = true
    receiveChannel?.close()
    receiveChannel?.removeEventListener('message', onMessageReceived)
    call.close()

    const elapsedTime = round(((now() - startTime) * 10) / 10000.0)
    const missingPayloadBytes = sentPayloadBytes - receivedPayloadBytes
    test.reportInfo(
      `Total sent: ${getReadableSizeString(
        sentPayloadBytes * 8,
      )} in ${elapsedTime} seconds.`,
    )
    test.reportInfo(
      `Total received: ${getReadableSizeString(
        receivedPayloadBytes * 8,
      )} in ${elapsedTime} seconds.`,
    )
    test.reportInfo(
      `Total missing: ${getReadableSizeString(
        missingPayloadBytes * 8,
      )} in ${elapsedTime} seconds.`,
    )
    test.reportFatal('DataChannelThroughputTest: Timed out')
  }

  try {
    call = new Call(test, config)
    call.setIceCandidateFilter(iceCandidateFilter)

    // Start test
    receiveChannel = null
    senderChannel = call.pc1.createDataChannel('DataChannelThroughputTest', {
      //negotiated: false,
      //ordered: false,
      //maxPacketLifeTime: 0
    })
    senderChannel.addEventListener('open', sendingStep)
    call.pc2.addEventListener('datachannel', onReceiverChannel)
    call.establishConnection()

    // Timout
    setTimeoutWithProgressBar(test, testTimeoutMs, testTimout)
  } catch (errDataChannel) {
    test.reportFatal(errDataChannel as Error)
  }
}

export const HostDataChannelThroughputTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    DataChannelThroughputTest.bind(null, test, Call.isHost),
    test.reportFatal.bind(test),
  )
}

// Set up a datachannel between two peers through a relay
// and verify data can be transmitted and received
// (packets travel through the public internet)
export const CandidateDataChannelThroughputTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    DataChannelThroughputTest.bind(null, test, Call.isNotHostCandidate),
    test.reportFatal.bind(test),
  )
}

// Set up a datachannel between two peers through a relay
// and verify data can be transmitted and received
// (packets travel through the public internet)
export const RelayDataChannelThroughputTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    DataChannelThroughputTest.bind(null, test, Call.isRelay),
    test.reportFatal.bind(test),
  )
}

// Set up a datachannel between two peers through a public IP address
// and verify data can be transmitted and received
// (packets should stay on the link if behind a router doing NAT)
export const ReflexiveDataChannelThroughputTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    DataChannelThroughputTest.bind(null, test, Call.isReflexiveOrHost),
    test.reportFatal.bind(test),
  )
}

//
// WiFiPeriodicScanTest
//

const arrayMin = (array: any[]) => {
  if (array.length === 0) {
    return NaN
  }
  return Math.min.apply(Math, array)
}

const arrayMax = (array: any[]) => {
  if (array.length === 0) {
    return NaN
  }
  return Math.max.apply(Math, array)
}

// array<function> returns the average (down to nearest int), max and min of
// an int array.
const arrayAverage = (array: number[]) => {
  const cnt = array.length
  let tot = 0
  for (let i = 0; i < cnt; i++) {
    tot += array[i]
  }
  return Math.floor(tot / cnt)
}

export const WiFiPeriodicScanTest = (
  test: Test,
  candidateFilter: (candidate: RTCIceCandidate) => boolean,
  config: any,
) => {
  test.reportInfo(
    'Use IceServers ' + JSON.stringify(config?.iceServers[0]?.urls),
  )

  let call: Call
  let senderChannel: RTCDataChannel
  const testDurationMs = 1 * 10 * 1000
  const testTimeoutMs = testDurationMs + 500
  const sendIntervalMs = 100
  let running = true
  const delays: number[] = []
  const recvTimeStamps: number[] = []

  //let chart = test.createLineChart();

  function receive(event: MessageEvent) {
    if (running) {
      const sendTime = parseInt(event.data, 10),
        time = now(),
        delay = time - sendTime

      if (delays.indexOf(delay) === -1) {
        test.reportInfo(`delay: ${round(delay)} ms.`)
      }

      recvTimeStamps.push(sendTime)
      delays.push(delay)
    }
  }

  function onReceiverChannel(event: RTCDataChannelEvent) {
    event.channel.addEventListener('message', receive)
  }

  function send() {
    if (running) {
      senderChannel.send('' + now())
      setTimeout(send, sendIntervalMs)
    }
  }

  function finishTest() {
    if (recvTimeStamps.length === 0) {
      test.reportFatal('Timed out')
      return
    }

    test.traceEventInstant('periodic-delay', {
      delays: delays,
      recvTimeStamps: recvTimeStamps,
    })
    running = false
    call.close()

    const avg = arrayAverage(delays)
    const max = arrayMax(delays)
    const min = arrayMin(delays)
    test.reportInfo(`Average delay: ${round(avg)} ms.`)
    test.reportInfo(`Min delay: ${round(min)}  ms.`)
    test.reportInfo(`Max delay: ${round(max)} ms.`)

    const minDelay = (0.79 * testDurationMs) / sendIntervalMs
    if (delays.length < minDelay) {
      test.reportWarning(
        'Not enough samples gathered. Keep the page on the ' +
          'foreground while the test is running.',
      )
    } else {
      test.reportSuccess('Collected ' + delays.length + ' delay samples.')
    }

    if (max > (min + 100) * 2) {
      test.reportWarning(
        'There is a big difference between the min and max delay ' +
          'of packets. Your network appears unstable.',
      )
    }
    test.done()
  }

  try {
    call = new Call(test, config)
    call.setIceCandidateFilter(candidateFilter)

    senderChannel = call.pc1.createDataChannel('WiFiPeriodicScanTest', {
      ordered: false,
      maxRetransmits: 0,
    })

    senderChannel.addEventListener('open', send)
    call.pc2.addEventListener('datachannel', onReceiverChannel)
    call.establishConnection()

    setTimeoutWithProgressBar(test, testTimeoutMs, finishTest)
  } catch (errCall) {
    test.reportFatal(errCall as Error)
  }
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const CandicateWiFiPeriodicScanTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    WiFiPeriodicScanTest.bind(null, test, Call.isNotHostCandidate),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const RelayWiFiPeriodicScanTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    WiFiPeriodicScanTest.bind(null, test, Call.isRelay),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const ReflexiveWiFiPeriodicScanTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    WiFiPeriodicScanTest.bind(null, test, Call.isReflexiveOrHost),
    test.reportFatal.bind(test),
  )
}

//
//
//

export const doGetUserMedia = (
  test: Test,
  constraints: MediaStreamConstraints,
  successFunc: (stream: MediaStream) => void,
  failFunc?: (err: Error) => void,
) => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  ) {
    test.reportInfo('Request UserMedia: ' + JSON.stringify(constraints))
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        test.reportInfo(`Got UserMedia: ${stream.id}`)
        return successFunc(stream)
      })
      .catch((err) => {
        test.reportInfo(`Failed UserMedia: ${err}`)
        if (failFunc) {
          failFunc(err)
        } else {
          throw err
        }
      })
  } else {
    const err = new Error('getUserMedia is not supported.')
    if (failFunc) {
      failFunc(err)
    } else {
      throw err
    }
  }
}

//
//
//

export class CamResolutionsTest {
  test: Test
  resolutions: number[][]

  videoElement?: HTMLVideoElement
  stream?: MediaStream
  frameChecker?: VideoFrameChecker

  mandatoryUnsupportedResolutions = 0
  counter = 0
  supportedResolutions = 0
  unsupportedResolutions = 0
  numResolutions = 0

  isMuted = false
  doBenchmarck = false

  constructor(test: Test, resolutionArray: number[][], doBenchmarck = false) {
    this.test = test
    this.resolutions = resolutionArray
    this.numResolutions = this.resolutions.length
    this.doBenchmarck = doBenchmarck
  }

  run() {
    return this.triggerGetUserMedia_(this.resolutions[0])
  }

  triggerGetUserMedia_(resolution: number[]) {
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        width: resolution[0],
        height: resolution[1],
      },
    }

    try {
      doGetUserMedia(
        this.test,
        constraints,
        this.successFunc_.bind(this),
        this.failFunc_.bind(this),
      )
    } catch (errGetUserMedia) {
      this.test.reportFatal(errGetUserMedia as Error)
    }

    return this.test
  }

  successFunc_(stream: MediaStream) {
    this.stream = stream
    this.supportedResolutions++
    const selectedResolution = this.resolutions[this.counter++]
    // Measure performance only when testing one resolution.

    if (this.doBenchmarck) {
      this.collectAndAnalyzeStats_(stream, selectedResolution)
    } else {
      this.test.reportInfo(
        'Supported ' + selectedResolution[0] + 'x' + selectedResolution[1],
      )
      stream.getVideoTracks()[0].stop()
      this.finishTestOrRetrigger_()
    }
  }

  collectAndAnalyzeStats_(stream: MediaStream, selectedResolution: number[]) {
    const tracks = stream.getVideoTracks()
    const test = this.test

    if (tracks.length < 1) {
      test.reportError('No video track in returned stream.')
      this.finishTestOrRetrigger_()
      return
    }

    const videoTrack = tracks[0]
    // Register events.
    videoTrack.onended = function () {
      test.reportError('Video track ended, camera stopped working')
    }
    videoTrack.onmute = () => {
      test.reportError('Your camera reported itself as muted.')
      // MediaStreamTrack.muted property is not wired up in Chrome yet, checking
      // isMuted local state.
      this.isMuted = true
    }
    videoTrack.onunmute = () => {
      this.isMuted = false
    }

    const video = (this.videoElement = document.createElement('video'))
    video.setAttribute('autoplay', '')
    video.setAttribute('muted', '')

    video.width = selectedResolution[0]
    video.height = selectedResolution[1]
    video.srcObject = stream

    const frameChecker = (this.frameChecker = new VideoFrameChecker(video))
    const call = new Call(this.test)

    call.pc1.addTrack(stream.getTracks()[0], stream)

    call.establishConnection()

    call.gatherStats(
      call.pc1,
      this.onCallEnded_.bind(this, selectedResolution),
      100,
    )

    setTimeoutWithProgressBar(this.test, 8000, call.close.bind(call))
  }

  onCallEnded_(
    selectedResolution: number[],
    stats: RTCStatsReport[],
    statsTime: number[],
  ) {
    this.analyzeStats_(selectedResolution, stats, statsTime)

    this.stream.getVideoTracks()[0].onended = null
    this.stream.getVideoTracks()[0].onmute = null
    this.stream.getVideoTracks()[0].onunmute = null
    this.stream.getVideoTracks()[0].stop()

    this.frameChecker?.stop()

    this.finishTestOrRetrigger_()
  }

  analyzeStats_(selectedResolution: number[], stats: any, statsTime: number[]) {
    const googAvgEncodeTime = []
    const googAvgFrameRateInput = []
    const googAvgFrameRateSent = []
    const frameStats = this.frameChecker?.frameStats

    let framesPerSecond = 0
    for (let index = 0; index < stats.length - 1; index++) {
      const report = stats[index]
      if (
        report.type === 'media-source' &&
        Object.hasOwn(report, 'framesPerSecond')
      ) {
        framesPerSecond = parseInt(report.framesPerSecond)
        googAvgFrameRateInput.push(framesPerSecond)
      }

      // Make sure to only capture stats after the encoder is setup.
      if (framesPerSecond > 0) {
        if (report.type === 'outbound-rtp') {
          if (Object.hasOwn(report, 'framesPerSecond')) {
            googAvgFrameRateSent.push(parseInt(report.framesPerSecond))
          }

          if (
            Object.hasOwn(report, 'totalEncodeTime') &&
            Object.hasOwn(report, 'framesEncoded')
          ) {
            googAvgEncodeTime.push(
              (report.totalEncodeTime * 100) / report.framesEncoded,
            )
          }
        }
      }
    }

    const statsTestReport = {
      mandatoryWidth: selectedResolution[0],
      mandatoryHeight: selectedResolution[1],
      isMuted: this.isMuted,
      cameraName: this.stream?.getVideoTracks()[0].label || NaN,
      actualVideoWidth: this.videoElement?.videoWidth || NaN,
      actualVideoHeight: this.videoElement?.videoHeight || NaN,
      encodeSetupTimeMs: round(this.extractEncoderSetupTime_(stats, statsTime)),
      avgEncodeTimeMs: round(arrayAverage(googAvgEncodeTime)),
      minEncodeTimeMs: round(arrayMin(googAvgEncodeTime)),
      maxEncodeTimeMs: round(arrayMax(googAvgEncodeTime)),
      avgInputFps: arrayAverage(googAvgFrameRateInput),
      minInputFps: arrayMin(googAvgFrameRateInput),
      maxInputFps: arrayMax(googAvgFrameRateInput),
      avgSentFps: arrayAverage(googAvgFrameRateSent),
      minSentFps: arrayMin(googAvgFrameRateSent),
      maxSentFps: arrayMax(googAvgFrameRateSent),
      testedFrames: frameStats.numFrames,
      blackFrames: frameStats.numBlackFrames,
      frozenFrames: frameStats.numFrozenFrames,
    }

    // TODO: Add a reportInfo() function with a table format to display
    // values clearer.
    this.test.traceEventInstant('video-stats', statsTestReport)

    this.testExpectations_(statsTestReport)
  }

  extractEncoderSetupTime_(stats: any[], statsTime: number[]) {
    for (let index = 0; index !== stats.length; index++) {
      const report = stats[index]
      if (
        report.type === 'media-source' &&
        Object.hasOwn(report, 'framesPerSecond')
      ) {
        if (report.framesPerSecond > 0) {
          return statsTime[index] - statsTime[0]
        }
      }
    }
    return NaN
  }

  testExpectations_(info: any) {
    const test = this.test
    const notAvailableStats = []
    for (const key in info) {
      if (info.hasOwnProperty(key)) {
        if (typeof info[key] === 'number' && isNaN(info[key])) {
          notAvailableStats.push(key)
        } else {
          test.reportInfo(key + ': ' + info[key])
        }
      }
    }
    if (notAvailableStats.length !== 0) {
      test.reportInfo('Not available: ' + notAvailableStats.join(', '))
    }

    if (isNaN(info.avgSentFps)) {
      test.reportInfo('Cannot verify sent FPS.')
    } else if (info.avgSentFps < 5) {
      test.reportError('Low average sent FPS: ' + info.avgSentFps)
    } else {
      test.reportSuccess('Average FPS above threshold')
    }
    if (
      !CamResolutionsTest.resolutionMatchesIndependentOfRotationOrCrop_(
        info.actualVideoWidth,
        info.actualVideoHeight,
        info.mandatoryWidth,
        info.mandatoryHeight,
      )
    ) {
      test.reportError('Incorrect captured resolution.')
    } else {
      test.reportSuccess('Captured video using expected resolution.')
    }
    if (info.testedFrames === 0) {
      test.reportError('Could not analyze any video frame.')
    } else {
      if (info.blackFrames > info.testedFrames / 3) {
        test.reportError('Camera delivering lots of black frames.')
      }
      if (info.frozenFrames > info.testedFrames / 3) {
        test.reportError('Camera delivering lots of frozen frames.')
      }
    }
  }

  failFunc_() {
    this.unsupportedResolutions++
    const selectedResolution = this.resolutions[this.counter++]
    if (selectedResolution[2]) {
      this.mandatoryUnsupportedResolutions++
      this.test.reportError(
        'Camera does not support a mandatory resolution: ' +
          selectedResolution[0] +
          'x' +
          selectedResolution[1],
      )
    } else {
      this.test.reportInfo(
        'NOT supported ' + selectedResolution[0] + 'x' + selectedResolution[1],
      )
    }
    this.finishTestOrRetrigger_()
  }

  finishTestOrRetrigger_() {
    if (this.counter === this.numResolutions) {
      if (this.mandatoryUnsupportedResolutions === 0) {
        if (this.supportedResolutions) {
          // Assume analyze video performance path if only one resolution.
          if (this.numResolutions > 1) {
            this.test.reportSuccess(
              this.supportedResolutions +
                '/' +
                this.numResolutions +
                ' resolutions supported.',
            )
          }
        } else {
          this.test.reportError(
            'No camera resolutions supported, most likely the ' +
              'camera is not accessible or dead.',
          )
        }
      }
      this.test.done()
    } else {
      this.triggerGetUserMedia_(this.resolutions[this.counter])
    }
  }

  static resolutionMatchesIndependentOfRotationOrCrop_(
    aWidth: number,
    aHeight: number,
    bWidth: number,
    bHeight: number,
  ) {
    const minRes = Math.min(bWidth, bHeight)
    return (
      (aWidth === bWidth && aHeight === bHeight) ||
      (aWidth === bHeight && aHeight === bWidth) ||
      (aWidth === minRes && bHeight === minRes)
    )
  }
}

export const MediaVideoSDTest = (test: Test) => {
  const cameraTest = new CamResolutionsTest(test, [[320, 240]], true)
  return cameraTest.run()
}

export const MediaVideoMDTest = (test: Test) => {
  const cameraTest = new CamResolutionsTest(test, [[640, 480]], true)
  return cameraTest.run()
}

export const MediaVideoHDTest = (test: Test) => {
  const cameraTest = new CamResolutionsTest(test, [[1280, 720]], true)
  return cameraTest.run()
}

export const MediaVideoResolutionsTest = (test: Test) => {
  // In generic cameras using Chrome rescaler, all resolutions should be supported
  // up to a given one and none beyond there. Special cameras, such as digitizers,
  // might support only one resolution.

  // Each resolution has width, height and 'mandatory' fields.
  const resolutions = [
    [160, 120],
    [320, 180],
    [320, 240],
    [640, 360],
    [640, 480],
    [768, 576], // PAL
    [1024, 576],
    [1280, 720],
    [1280, 768],
    [1280, 800],
    [1920, 1080], // Full HD
    [1920, 1200],
    [3840, 2160], // 4K
    [4096, 2160],
  ]

  const cameraTest = new CamResolutionsTest(test, resolutions)
  return cameraTest.run()
}

//
//
//

// Measures video bandwidth estimation performance by doing a loopback call via
// relay candidates for 40 seconds. Computes rtt and bandwidth estimation
// average and maximum as well as time to ramp up (defined as reaching 75% of
// the max bitrate. It reports infinite time to ramp up if never reaches it.

const VideoBandwidthTest = (
  test: Test,
  iceCandidateFilter: (candidate: RTCIceCandidate) => boolean,
  config: any,
) => {
  test.reportInfo(
    'Use IceServers ' + JSON.stringify(config?.iceServers[0]?.urls),
  )

  const maxVideoBitrateKbps = 2000
  const testDurationMs = 5 * 1000
  const testTimeoutMs = testDurationMs * 1.25
  const statStepMs = 100
  const rampUpThreshold = 0.75 * maxVideoBitrateKbps * 1000
  const bweStats = new StatisticsAggregate(rampUpThreshold)
  const rttStats = new StatisticsAggregate(rampUpThreshold)
  let packetsLost = 0
  const videoStats: any[] = [-1, -1]
  let startTime = 0

  const call = new Call(test, config)
  call.setIceCandidateFilter(iceCandidateFilter)
  call.constrainVideoBitrate(maxVideoBitrateKbps)

  // FEC makes it hard to study bandwidth estimation since there seems to be
  // a spike when it is enabled and disabled. Disable it for now. FEC issue
  // tracked on: https://code.google.com/p/webrtc/issues/detail?id=3050
  //call.disableVideoFec();

  function end() {
    //call.pc1.getLocalStreams()[0].getVideoTracks()[0].stop()
    call.pc1.getSenders().forEach((sender) => sender.track?.stop())

    call.close()

    test.reportInfo('RTT average: ' + rttStats.getAverage() + ' ms')
    test.reportInfo('RTT max: ' + rttStats.getMax() + ' ms')
    test.reportInfo('Lost packets: ' + packetsLost)

    test.reportInfo(
      `Send bandwidth estimate average: ${getReadableSizeString(
        bweStats.getAverage(),
      )}`,
    )
    test.reportInfo(
      `Send bandwidth estimate max: ${getReadableSizeString(
        bweStats.getMax(),
      )}`,
    )
    test.reportInfo(
      `Send bandwidth ramp-up time: ${bweStats.getRampUpTime()} ms`,
    )
  }

  function completed() {
    end()

    // Checking if greater than 2 because Chrome sometimes reports 2x2 when
    // a camera starts but fails to deliver frames.
    if (videoStats[0] < 3 && videoStats[1] < 3) {
      test.reportError(
        'Camera failure: ' +
          videoStats[0] +
          'x' +
          videoStats[1] +
          '. Cannot test bandwidth without a working camera.',
      )
    } else {
      test.reportSuccess(
        'Video resolution: ' + videoStats[0] + 'x' + videoStats[1],
      )
    }
    test.done()
  }

  const gotStats = (stats: RTCStatsReport) => {
    stats.forEach((report) => {
      if (report.type === 'candidate-pair') {
        if (Object.hasOwn(report, 'availableOutgoingBitrate')) {
          bweStats.add(
            report.timestamp,
            parseInt(report.availableOutgoingBitrate),
          )
        }

        if (Object.hasOwn(report, 'currentRoundTripTime')) {
          rttStats.add(report.timestamp, parseInt(report.currentRoundTripTime))
        }
      }

      if (
        report.type === 'outbound-rtp' &&
        Object.hasOwn(report, 'frameWidth')
      ) {
        // Grab the last stats.
        videoStats[0] = parseInt(report.frameWidth)
        videoStats[1] = parseInt(report.frameHeight)
      } else if (
        report.type === 'inbound-rtp' &&
        Object.hasOwn(report, 'packetsLost')
      ) {
        packetsLost = parseInt(report.packetsLost)
      }
    })

    setTimeout(gatherStats, statStepMs)
  }

  const gatherStats = function () {
    const now = new Date().getTime()
    if (now - startTime > testDurationMs) {
      test.setProgress(100)
      completed()
    } else {
      test.setProgress(((now - startTime) * 100) / testDurationMs)

      try {
        call.pc1.getStats().then(gotStats)
      } catch (gatherStatsErr) {
        test.reportError(gatherStatsErr as Error)
      }
    }
  }

  function gotStream(stream: MediaStream) {
    call.pc1.addTrack(stream.getTracks()[0], stream)
    call.establishConnection()
  }

  function timeoutTest() {
    end()
    test.reportFatal('Timed out')
  }

  // Open the camera in 720p to get a correct measurement of ramp-up time.
  const constraints: MediaStreamConstraints = {
    audio: false,
    video: {
      width: 1280,
      height: 720,
    },
  }

  try {
    //
    doGetUserMedia(test, constraints, gotStream)

    startTime = new Date().getTime()

    // Timoute
    setTimeoutWithProgressBar(test, testTimeoutMs, timeoutTest)

    gatherStats()
  } catch (errGetUserMedia) {
    test.reportFatal(errGetUserMedia as Error)
  }
}

export const HostVideoBandwidthTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    VideoBandwidthTest.bind(null, test, Call.isHost),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const CandidateVideoBandwidthTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    VideoBandwidthTest.bind(null, test, Call.isNotHostCandidate),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const RelayVideoBandwidthTest = (test: Test) => {
  return Call.asyncCreateTurnConfig(
    test,
    VideoBandwidthTest.bind(null, test, Call.isRelay),
    test.reportFatal.bind(test),
  )
}

// Test whether it can connect via TCP to a TURN server
// Get a TURN config, and try to get a relay candidate using TCP.
export const ReflexiveVideoBandwidthTest = (test: Test) => {
  return Call.asyncCreateStunConfig(
    test,
    VideoBandwidthTest.bind(null, test, Call.isReflexiveOrHost),
    test.reportFatal.bind(test),
  )
}

//
//
//

let audioContext: AudioContext
function getAudioContext() {
  // Global WebAudio context that can be shared by all tests.
  // There is a very finite number of WebAudio contexts.
  if (!audioContext) {
    try {
      audioContext = new window.AudioContext()
    } catch (e) {
      // throw Error('Failed to instantiate an audio context, error: ' + e);
    }
  }

  return audioContext
}

export class MicTest {
  test: Test
  stream!: MediaStream
  audioSource: any
  scriptNode: any
  stopCollectingAudio: any

  inputChannelCount = 6
  outputChannelCount = 2

  // Buffer size set to 0 to let Chrome choose based on the platform.
  bufferSize = 0

  // Turning off echoCancellation constraint enables stereo input.
  constraints: MediaStreamConstraints = {
    audio: {
      echoCancellation: false,
    },
  }

  // At least one LSB 16-bit data (compare is on absolute value).
  collectSeconds = 2.0
  silentThreshold = 1.0 / 32767
  lowVolumeThreshold = -60

  // Data must be identical within one LSB 16-bit to be identified as mono.
  monoDetectThreshold = 1.0 / 65536

  // Number of consequtive clipThreshold level samples that indicate clipping.
  clipCountThreshold = 6
  clipThreshold = 1.0

  // Populated with audio as a 3-dimensional array:
  //   collectedAudio[channels][buffers][samples]
  collectedAudio: any[] = []
  collectedSampleCount = 0

  constructor(test: Test) {
    this.test = test
    for (let i = 0; i < this.inputChannelCount; ++i) {
      this.collectedAudio[i] = []
    }
  }

  run() {
    const audioContext = getAudioContext()
    if (typeof audioContext === 'undefined') {
      this.test.reportError('WebAudio is not supported, test cannot run.')
      this.test.done()
    } else {
      try {
        doGetUserMedia(this.test, this.constraints, this.gotStream.bind(this))
      } catch (errGetUserMedia) {
        this.test.reportFatal(errGetUserMedia as Error)
      }
    }
  }

  gotStream(stream: MediaStream) {
    if (!this.checkAudioTracks(stream)) {
      this.test.done()
      return
    }
    this.createAudioBuffer(stream)
  }

  checkAudioTracks(stream: MediaStream) {
    const audioTracks = stream.getAudioTracks()
    if (audioTracks.length < 1) {
      this.test.reportError('No audio track in returned stream.')
      return false
    }
    this.test.reportInfo(
      'Audio track created using device=' + audioTracks[0].label,
    )
    return true
  }

  createAudioBuffer(stream: MediaStream) {
    this.stream = stream
    const audioContext = getAudioContext()
    this.audioSource = audioContext.createMediaStreamSource(this.stream)
    this.scriptNode = audioContext.createScriptProcessor(
      this.bufferSize,
      this.inputChannelCount,
      this.outputChannelCount,
    )
    this.audioSource.connect(this.scriptNode)
    this.scriptNode.connect(audioContext.destination)
    this.scriptNode.onaudioprocess = this.collectAudio.bind(this)
    this.stopCollectingAudio = setTimeoutWithProgressBar(
      this.test,
      5000,
      this.onStopCollectingAudio.bind(this),
    )
  }

  collectAudio(event: any) {
    // Simple silence detection: check first and last sample of each channel in
    // the buffer. If both are below a threshold, the buffer is considered
    // silent.
    const sampleCount = event.inputBuffer.length
    let allSilent = true
    for (let c = 0; c < event.inputBuffer.numberOfChannels; c++) {
      const data = event.inputBuffer.getChannelData(c)
      const first = Math.abs(data[0])
      const last = Math.abs(data[sampleCount - 1])
      let newBuffer
      if (first > this.silentThreshold || last > this.silentThreshold) {
        // Non-silent buffers are copied for analysis. Note that the silent
        // detection will likely cause the stored stream to contain discontinu-
        // ities, but that is ok for our needs here (just looking at levels).
        newBuffer = new Float32Array(sampleCount)
        newBuffer.set(data)
        allSilent = false
      } else {
        // Silent buffers are not copied, but we store empty buffers so that the
        // analysis doesn't have to care.
        newBuffer = new Float32Array([])
      }
      this.collectedAudio[c].push(newBuffer)
    }
    if (!allSilent) {
      this.collectedSampleCount += sampleCount
      if (
        this.collectedSampleCount / event.inputBuffer.sampleRate >=
        this.collectSeconds
      ) {
        this.stopCollectingAudio()
      }
    }
  }

  onStopCollectingAudio() {
    const audioContext = getAudioContext()
    this.stream.getAudioTracks()[0].stop()
    this.audioSource.disconnect(this.scriptNode)
    this.scriptNode.disconnect(audioContext.destination)
    this.analyzeAudio(this.collectedAudio)
    this.test.done()
  }

  analyzeAudio(channels: any[]) {
    const activeChannels = []
    for (let c = 0; c < channels.length; c++) {
      if (this.channelStats(c, channels[c])) {
        activeChannels.push(c)
      }
    }
    if (activeChannels.length === 0) {
      this.test.reportError(
        'No active input channels detected. Microphone is most ' +
          'likely muted or broken, please check if muted in the ' +
          'sound settings or physically on the device. Then rerun ' +
          'the test.',
      )
    } else {
      this.test.reportSuccess(
        'Active audio input channels: ' + activeChannels.length,
      )
    }
    if (activeChannels.length === 2) {
      this.detectMono(channels[activeChannels[0]], channels[activeChannels[1]])
    }
  }

  channelStats(channelNumber: any, buffers: any) {
    let maxPeak = 0.0
    let maxRms = 0.0
    let clipCount = 0
    let maxClipCount = 0
    for (let j = 0; j < buffers.length; j++) {
      const samples = buffers[j]
      if (samples.length > 0) {
        let s = 0
        let rms = 0.0
        for (let i = 0; i < samples.length; i++) {
          s = Math.abs(samples[i])
          maxPeak = Math.max(maxPeak, s)
          rms += s * s
          if (maxPeak >= this.clipThreshold) {
            clipCount++
            maxClipCount = Math.max(maxClipCount, clipCount)
          } else {
            clipCount = 0
          }
        }
        // RMS is calculated over each buffer, meaning the integration time will
        // be different depending on sample rate and buffer size. In practise
        // this should be a small problem.
        rms = Math.sqrt(rms / samples.length)
        maxRms = Math.max(maxRms, rms)
      }
    }

    if (maxPeak > this.silentThreshold) {
      const dBPeak = this.dBFS(maxPeak)
      const dBRms = this.dBFS(maxRms)
      this.test.reportInfo(
        'Channel ' +
          channelNumber +
          ' levels: ' +
          dBPeak.toFixed(1) +
          ' dB (peak), ' +
          dBRms.toFixed(1) +
          ' dB (RMS)',
      )
      if (dBRms < this.lowVolumeThreshold) {
        this.test.reportWarning(
          'Microphone input level is low, increase input ' +
            'volume or move closer to the microphone.',
        )
      }
      if (maxClipCount > this.clipCountThreshold) {
        this.test.reportWarning(
          'Clipping detected! Microphone input level is high. ' +
            'Decrease input volume or move away from the ' +
            'microphone.',
        )
      }
      return true
    } else {
      return false
    }
  }

  detectMono(buffersL: any, buffersR: any) {
    let diffSamples = 0
    for (let j = 0; j < buffersL.length; j++) {
      const l = buffersL[j]
      const r = buffersR[j]
      if (l.length === r.length) {
        let d = 0.0
        for (let i = 0; i < l.length; i++) {
          d = Math.abs(l[i] - r[i])
          if (d > this.monoDetectThreshold) {
            diffSamples++
          }
        }
      } else {
        diffSamples++
      }
    }
    if (diffSamples > 0) {
      this.test.reportInfo('Stereo microphone detected.')
    } else {
      this.test.reportInfo('Mono microphone detected.')
    }
  }

  dBFS(gain: number) {
    const dB = (20 * Math.log(gain)) / Math.log(10)
    // Use Math.round to display up to one decimal place.
    return Math.round(dB * 10) / 10
  }
}

export const MediaAudioTest = (test: Test) => {
  const audioTest = new MicTest(test)
  audioTest.run()
}
